import React, { useState, useEffect } from 'react';
import { HStack, Box, Text, VStack, useColorModeValue } from '@chakra-ui/react';
import api from '../../services/api';

const MetricCard = ({ label, value, onClick, variant = 'default', isActive = false }) => {
  // Color mode values
  const bgColor = useColorModeValue('white', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedTextColor = useColorModeValue('gray.600', 'gray.300');
  const activeTextColor = useColorModeValue('blue.600', 'blue.200');
  const activeBgColor = useColorModeValue('blue.50', 'blue.900');
  const warningBgColor = useColorModeValue('red.50', 'red.900');
  const dangerBgColor = useColorModeValue('red.50', 'red.900');
  const borderColor = useColorModeValue(
    variant === 'danger' ? 'red.400' : isActive ? 'blue.400' : 'gray.200',
    variant === 'danger' ? 'red.400' : isActive ? 'blue.400' : 'gray.600'
  );
  const hoverDangerBg = useColorModeValue('red.100', 'red.800');
  const hoverWarningBg = useColorModeValue('red.100', 'red.800');
  const hoverActiveBg = useColorModeValue('blue.100', 'blue.800');
  const hoverDefaultBg = useColorModeValue('gray.50', 'gray.600');
  const activeDotColor = useColorModeValue('blue.400', 'blue.200');

  // Determine background color based on variant and active state
  const bg = variant === 'danger' 
    ? dangerBgColor 
    : variant === 'warning' 
    ? warningBgColor 
    : isActive 
    ? activeBgColor 
    : bgColor;

  // Determine hover background color
  const getHoverBg = () => {
    if (variant === 'danger') return hoverDangerBg;
    if (variant === 'warning') return hoverWarningBg;
    if (isActive) return hoverActiveBg;
    return hoverDefaultBg;
  };

  return (
    <Box
      bg={bg}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="md"
      px={3}
      py={1}
      cursor="pointer"
      onClick={onClick}
      transition="all 0.2s"
      _hover={{ 
        borderColor: variant === 'danger' ? 'red.500' : isActive ? 'blue.500' : 'gray.300',
        bg: getHoverBg()
      }}
      position="relative"
      minW="120px"
      h="100%"
      display="flex"
      alignItems="center"
    >
      <HStack spacing={2} align="center" w="100%" justify="center">
        <Text 
          fontSize="md" 
          fontWeight="bold" 
          color={isActive ? activeTextColor : textColor}
        >
          {value}
        </Text>
        <Text 
          fontSize="sm" 
          color={isActive ? activeTextColor : mutedTextColor}
          fontWeight={isActive ? 'medium' : 'normal'}
        >
          {label}
        </Text>
      </HStack>
      {isActive && (
        <Box
          position="absolute"
          top="-1px"
          right="-1px"
          w="6px"
          h="6px"
          bg={activeDotColor}
          borderRadius="full"
        />
      )}
    </Box>
  );
};

const ActionableMetrics = ({ onMetricClick, activeMetric = null }) => {
  const [metrics, setMetrics] = useState({
    todayFollowUps: 0,
    missedFollowUps: 0,
    noFollowUp5d: 0,
    proposalStage: 0,
    noCalls: 0
  });

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const response = await api.get('/api/analytics/actionable-metrics');
        setMetrics(response.data);
      } catch (error) {
        console.error('Failed to fetch metrics:', error);
      }
    };

    fetchMetrics();
    const interval = setInterval(fetchMetrics, 300000); // Refresh every 5 minutes
    return () => clearInterval(interval);
  }, []);

  const handleMetricClick = (metricType) => {
    if (metricType === activeMetric) {
      onMetricClick(null); // Clear filter
    } else {
      onMetricClick(metricType);
    }
  };

  return (
    <HStack spacing={2} overflowX="auto" h="40px">
      <MetricCard
        label="No Calls"
        value={metrics.noCalls}
        onClick={() => handleMetricClick('noCalls')}
        variant="danger"
        isActive={activeMetric === 'noCalls'}
      />
      <MetricCard
        label="Missed Follow-ups"
        value={metrics.missedFollowUps}
        onClick={() => handleMetricClick('missedFollowUps')}
        variant="warning"
        isActive={activeMetric === 'missedFollowUps'}
      />
      <MetricCard
        label="Today's Follow-ups"
        value={metrics.todayFollowUps}
        onClick={() => handleMetricClick('todayFollowUps')}
        isActive={activeMetric === 'todayFollowUps'}
      />
      <MetricCard
        label="No Follow-up"
        value={metrics.noFollowUp5d}
        onClick={() => handleMetricClick('noFollowUp5d')}
        isActive={activeMetric === 'noFollowUp5d'}
      />
      <MetricCard
        label="Proposal Stage"
        value={metrics.proposalStage}
        onClick={() => handleMetricClick('proposalStage')}
        isActive={activeMetric === 'proposalStage'}
      />
    </HStack>
  );
};

export default ActionableMetrics;
